import { type FC, useRef } from "react";
import { AnimatedTooltip } from "widgets";
import { useTooltip } from "widgets/hooks";
import { colors } from "widgets/utils";
import { useShallow } from "zustand/shallow";

import { useGenomeViewerStore } from "../../store/store.ts";
import { useGroupHeight } from "./hooks/use-group-height.ts";
import { useGroupWidth } from "./hooks/use-group-width.ts";
import { SVG_STYLES } from "./settings.ts";

interface TrackGroupItemProps {
  groupId: string;
  scale: number;
  panelWidth: number;
  isGroupHovered?: boolean;
}

export const TrackGroupItem: FC<TrackGroupItemProps> = ({ groupId, scale, isGroupHovered }) => {
  const ref = useRef<SVGRectElement | null>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(ref, { closeDelay: 0 });

  const { organismName, contigName } = useGenomeViewerStore(
    useShallow((state) => {
      const trackGroup = state.trackGroups.get(groupId)!;
      return { organismName: trackGroup.organismName, contigName: trackGroup.contigName };
    }),
  );

  const width = useGroupWidth(groupId, scale);
  const groupHeight = useGroupHeight();

  return (
    <>
      <rect
        x={(SVG_STYLES.group.strokeWidth / 2) % 2}
        y={(SVG_STYLES.group.strokeWidth / 2) % 2}
        width={Math.round(width)}
        height={groupHeight}
        {...SVG_STYLES.group}
        ref={ref}
        {...triggerProps}
        fill={isGroupHovered ? colors.primaryPale + "6F" : SVG_STYLES.group.fill}
      />
      <AnimatedTooltip
        state={state}
        domProps={tooltipProps}
        targetRef={ref}
        title={organismName}
        placement="left"
        offset={5}
        nonInteractive
      >
        {contigName}
      </AnimatedTooltip>
    </>
  );
};
